import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n';
import { Attrib, DELIVERY_OPTIONS, FlagKey } from './CategorySidebarFilters';

interface SelectedFiltersProps {
  attribs: Attrib[];
  selectedParameters: string[];
  onParametersChange: (parameters: string[]) => void;
  selectedFlags: string[];
  onFlagsChange: (flags: string[]) => void;
  priceValue: { min: string; max: string };
  onPriceChange: (min: string, max: string) => void;
  selectedDeliveryDays: string[];
  onDeliveryDaysChange: (deliveryDays: string[]) => void;
}

const FLAG_LABELS: Record<FlagKey, string> = {
  sale: __('Akcia'),
  new: __('Novinka'),
  saleout: __('Výpredaj'),
  top: __('Tip'),
};

const Group = styled.div`
  display: inline-flex;
  align-items: center;
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  height: ${rem(36)};
  padding: 0 ${rem(16)};
  background: ${({ theme }) => theme.color.white};
  border: ${rem(1)} solid #e0e0e0;
  border-radius: ${rem(4)} 0 0 ${rem(4)};
  font-size: ${rem(14)};
  font-weight: 600;
  line-height: ${rem(20)};
  color: #171717;
  white-space: nowrap;
`;

const Chip = styled.button<{ last: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  height: ${rem(36)};
  margin-left: -${rem(1)};
  padding: 0 ${rem(12)};
  border: ${rem(1)} solid #d6d6d6;
  border-radius: ${({ last }) => (last ? `0 ${rem(4)} ${rem(4)} 0` : '0')};
  background: #ebebeb;
  color: #171717;
  font-family: inherit;
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    background: #e0e0e0;
  }
`;

const CloseIcon = styled.svg`
  width: ${rem(18)};
  height: ${rem(18)};
  flex: 0 0 auto;
`;

interface ChipData {
  key: string;
  label: string;
  onRemove: () => void;
}

const SelectedFilters: React.FC<SelectedFiltersProps> = ({
  attribs,
  selectedParameters,
  onParametersChange,
  selectedFlags,
  onFlagsChange,
  priceValue,
  onPriceChange,
  selectedDeliveryDays,
  onDeliveryDaysChange,
}) => {
  const removeParam = (param: string) =>
    onParametersChange(selectedParameters.filter((p) => p !== param));

  const chips: ChipData[] = [];

  if (priceValue.min || priceValue.max) {
    let label: string;
    if (priceValue.min && priceValue.max) {
      label = `${priceValue.min} – ${priceValue.max} €`;
    } else if (priceValue.min) {
      label = `${__('od')} ${priceValue.min} €`;
    } else {
      label = `${__('do')} ${priceValue.max} €`;
    }
    chips.push({ key: 'price', label, onRemove: () => onPriceChange('', '') });
  }

  selectedDeliveryDays.forEach((bucket) => {
    const option = DELIVERY_OPTIONS.find((o) => o.key === bucket);
    chips.push({
      key: `delivery-${bucket}`,
      label: `${__('Dodanie')} ${option?.label || bucket}`,
      onRemove: () =>
        onDeliveryDaysChange(selectedDeliveryDays.filter((d) => d !== bucket)),
    });
  });

  selectedFlags.forEach((flag) => {
    chips.push({
      key: `flag-${flag}`,
      label: FLAG_LABELS[flag as FlagKey] || flag,
      onRemove: () => onFlagsChange(selectedFlags.filter((f) => f !== flag)),
    });
  });

  selectedParameters.forEach((param) => {
    const rangeMatch = param.match(/^(\d+):(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
    if (rangeMatch) {
      const attrib = attribs.find(
        (a) => a.attrib_id === parseInt(rangeMatch[1]),
      );
      const unit = attrib?.attrib_unit ? ` ${attrib.attrib_unit}` : '';
      const name = attrib?.attrib_name ? `${attrib.attrib_name}: ` : '';
      chips.push({
        key: param,
        label: `${name}${rangeMatch[2]} – ${rangeMatch[3]}${unit}`,
        onRemove: () => removeParam(param),
      });
      return;
    }
    const termMatch = param.match(/^(\d+)-(\d+)$/);
    if (termMatch) {
      const attrib = attribs.find(
        (a) => a.attrib_id === parseInt(termMatch[1]),
      );
      const value = attrib?.values.find(
        (v) => v.value_id === parseInt(termMatch[2]),
      );
      chips.push({
        key: param,
        label: value?.value_text || param,
        onRemove: () => removeParam(param),
      });
    }
  });

  if (chips.length === 0) {
    return null;
  }

  return (
    <Group>
      <Label>{__('Filter')}</Label>
      {chips.map((chip, i) => (
        <Chip
          key={chip.key}
          type="button"
          last={i === chips.length - 1}
          onClick={chip.onRemove}
          title={__('Zrušiť filter')}
        >
          <span>{chip.label}</span>
          <CloseIcon viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </CloseIcon>
        </Chip>
      ))}
    </Group>
  );
};

export default SelectedFilters;
